// Scoring. The one genuinely non-obvious rule in No Thanks!, and the one a
// digital version has to get exactly right because nobody can check it by eye.
//
// "If you take cards with consecutive numbers, only the lowest number in the
// series will count against you. A series can be made up of as few as two
// cards." (p3, Game Play 5)
//
// "Add up the points on all the cards you took (only count the lowest card in
// a series), then subtract the number of counters you have. The player with
// the lowest total wins." (p3, Winning the Game)
//
// The rulebook's own worked examples, both reproduced as tests:
//   13, 15, 16       -> 13 + 15         = 28   (15 is lowest of the 15-16 run)
//   13, 14, 15, 16   -> 13              = 13   (one run now; the 14 absorbed it)
//   3, 7, 12, 15, 25 -> 62, minus 8 counters = 54
//
// The second example is the one worth internalizing: taking a LOWER card can
// collapse two runs into one and wipe out a whole scoring term. That is the
// entire strategic point of the game, so this must not be approximated.

/**
 * Groups a player's cards into ascending runs of consecutive values.
 * Returns an array of arrays, each inner array sorted ascending.
 *
 * Duplicates are impossible in a real game (each of the 33 cards is unique),
 * but a repeated value would silently break run detection, so it's collapsed
 * here rather than trusted.
 */
export const groupIntoRuns = (cards) => {
  const sorted = [...new Set(cards)].sort((a, b) => a - b);
  const runs = [];
  let current = [];

  for (const value of sorted) {
    if (current.length === 0 || value === current[current.length - 1] + 1) {
      current.push(value);
    } else {
      runs.push(current);
      current = [value];
    }
  }
  if (current.length > 0) runs.push(current);

  return runs;
};

/**
 * The card portion of a score: the lowest card of each run, summed.
 * A lone card is a run of one and counts in full.
 */
export const cardPoints = (cards) => groupIntoRuns(cards)
  .reduce((total, run) => total + run[0], 0);

/**
 * Final score for one player: card points minus counters. Lower is better,
 * and a score can legitimately be negative (11 counters and one cheap card
 * beats an empty hand).
 */
export const scoreFor = ({ cards = [], counters = 0 }) => cardPoints(cards) - counters;

/**
 * Full scoreboard, best (lowest) first.
 *
 * Ties: the rulebook says only "The player with the lowest total wins" and
 * prints no tie-break at all, so a tie is a genuine shared win rather than
 * something to invent a rule for. Every player on the lowest score gets
 * `isWinner: true`, and `rank` is competition-style (1, 1, 3) so a two-way
 * tie for first is visibly a tie rather than an arbitrary ordering.
 */
export const scoreboard = (players) => {
  const scored = players.map((player) => ({
    id: player.id,
    name: player.name,
    cards: [...player.cards].sort((a, b) => a - b),
    runs: groupIntoRuns(player.cards),
    counters: player.counters,
    cardPoints: cardPoints(player.cards),
    score: scoreFor(player),
  }));

  return rankByScore(scored);
};

/**
 * Sorts rows by `score`, lowest first, and adds competition ranks and the
 * shared-win flag. The one ranking rule, used for a single game and for a
 * match total alike.
 */
const rankByScore = (rows) => {
  const scored = [...rows].sort((a, b) => a.score - b.score);

  const best = scored.length > 0 ? scored[0].score : 0;
  let rank = 0;
  let seen = 0;
  let previous = null;

  return scored.map((entry) => {
    seen += 1;
    if (entry.score !== previous) {
      rank = seen;
      previous = entry.score;
    }
    return { ...entry, rank, isWinner: entry.score === best };
  });
};

/**
 * A multi-round match (house rule - see constants.js, MAX_ROUNDS): the
 * lowest TOTAL across every banked round wins, by the same rule and with the
 * same no-tie-break as a single game. Each row keeps this round's own
 * result as `round*` fields; `score`, `rank` and `isWinner` are the match's.
 */
export const matchStandings = (players, roundResults) => {
  const total = (id) => roundResults.reduce((sum, r) => sum + (r.scores?.[id] ?? 0), 0);
  return rankByScore(scoreboard(players).map((entry) => ({
    ...entry,
    roundScore: entry.score,
    roundRank: entry.rank,
    score: total(entry.id),
  })));
};
